import { Camera, Upload } from "lucide-react";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { BaseUserResponse } from "../../auth/data";
import LanguageSwitcher from "../../../components/common/LanguageSwitcher";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectV2,
  SelectValue,
} from "../../../components";
import { useLocalization } from "../../../hooks/useLocalization";
import type { ProfileFormValues } from "../schemas/profile.schema";

interface ProfileFormProps {
  form: UseFormReturn<ProfileFormValues>;
  user: BaseUserResponse | null;
  disabled: boolean;
  onChangePassword: () => void;
}

const fieldErrorClass = "border-red-500 focus:border-red-500 focus:ring-red-500";
const fieldErrorMessageClass = "text-sm text-red-600 dark:text-red-400";

export const ProfileForm = ({ form, user, disabled, onChangePassword }: ProfileFormProps) => {
  const { t } = useTranslation();
  const { currentLanguage, getSupportedLanguages } = useLocalization();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarPreview, setAvatarPreview] = useState(user?.profile_image_url || "");
  const {
    control,
    register,
    setValue,
    watch,
    formState: { errors },
  } = form;
  const role = watch("role");
  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const currentLanguageName =
    getSupportedLanguages()?.find((language) => language?.code === currentLanguage)?.name ||
    "English";

  const handleAvatarChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setValue("profileImageFile", file, { shouldValidate: true });

    if (file) {
      const reader = new FileReader();
      reader.onload = (loadEvent) => {
        setAvatarPreview((loadEvent.target?.result as string) || "");
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    if (user?.profile_image_url) {
      setAvatarPreview(user.profile_image_url);
    }
  }, [user?.profile_image_url]);

  const roles = [
    { value: "super_admin", label: t("common.super_admin") },
    { value: "admin", label: t("common.admin") },
    { value: "developer", label: t("common.developer") },
    { value: "user", label: t("common.user") },
  ];
  const genders = [
    { value: "male", label: t("common.male") },
    { value: "female", label: t("common.female") },
    { value: "other", label: t("common.other") },
  ];
  const roleLabels: Record<string, string> = Object.fromEntries(
    roles.map(({ label, value }) => [value, label]),
  );

  return (
    <div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>{t("profile.profile_picture")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <Avatar className="h-32 w-32">
                  <AvatarImage
                    src={avatarPreview}
                    alt="Profile"
                  />
                  <AvatarFallback className="text-2xl">
                    {firstName.charAt(0)}
                    {lastName.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <label className="absolute bottom-0 right-0 cursor-pointer rounded-full bg-primary p-2 text-primary-foreground transition-colors hover:bg-primary/90">
                  <Camera className="h-4 w-4" />
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={disabled}
                    onChange={handleAvatarChange}
                  />
                </label>
              </div>
              <div className="space-y-2 text-center">
                <p className="text-sm text-muted-foreground">{t("profile.image_format_hint")}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                  disabled={disabled}
                  onClick={() => avatarInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4" />
                  {t("profile.upload_new_photo")}
                </Button>
                {errors.profileImageFile?.message ? (
                  <p className={fieldErrorMessageClass}>{errors.profileImageFile.message}</p>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>{t("profile.personal_information")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t("profile.first_name")}</Label>
                <Input
                  id="firstName"
                  disabled={disabled}
                  placeholder={t("profile.enter_first_name")}
                  aria-invalid={Boolean(errors.firstName)}
                  className={errors.firstName ? fieldErrorClass : undefined}
                  {...register("firstName")}
                />
                {errors.firstName?.message ? (
                  <p className={fieldErrorMessageClass}>{errors.firstName.message}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{t("profile.last_name")}</Label>
                <Input
                  id="lastName"
                  disabled={disabled}
                  placeholder={t("profile.enter_last_name")}
                  aria-invalid={Boolean(errors.lastName)}
                  className={errors.lastName ? fieldErrorClass : undefined}
                  {...register("lastName")}
                />
                {errors.lastName?.message ? (
                  <p className={fieldErrorMessageClass}>{errors.lastName.message}</p>
                ) : null}
              </div>
            </div>

            <div className="flex items-end gap-6">
              <div className="flex-1 space-y-2">
                <Label htmlFor="email">{t("profile.email_address")}</Label>
                <Input
                  id="email"
                  type="email"
                  value={user?.email || ""}
                  disabled
                  placeholder={t("profile.enter_email_address")}
                  readOnly
                />
              </div>
              <Button
                type="button"
                variant="outline"
                className="h-10 border-[#004232] px-4 text-[#004232] hover:bg-[#004232]/5 dark:border-[#00D6A8] dark:text-[#00D6A8] dark:hover:bg-[#00D6A8]/10"
                onClick={onChangePassword}
              >
                {t("profile.change_password")}
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t("profile.phone_number")}</Label>
              <div className="flex gap-2">
                <div className="flex w-32 items-center justify-center rounded-md border border-input bg-muted px-3 py-2 text-sm">
                  US +1
                </div>
                <Input
                  id="phone"
                  disabled={disabled}
                  placeholder={t("profile.enter_phone_number")}
                  aria-invalid={Boolean(errors.phoneNumber)}
                  className={`flex-1${errors.phoneNumber ? ` ${fieldErrorClass}` : ""}`}
                  {...register("phoneNumber")}
                />
              </div>
              {errors.phoneNumber?.message ? (
                <p className={fieldErrorMessageClass}>{errors.phoneNumber.message}</p>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="role">{t("profile.role")}</Label>
                <Controller
                  control={control}
                  name="role"
                  render={({ field }) => (
                    <SelectV2
                      value={field.value}
                      onChange={field.onChange}
                      options={roles}
                      ariaLabel={t("profile.role")}
                      disabled
                    />
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gender">{t("profile.gender")}</Label>
                <Controller
                  control={control}
                  name="gender"
                  render={({ field }) => (
                    <SelectV2
                      value={field.value}
                      onChange={field.onChange}
                      options={genders}
                      ariaLabel={t("profile.gender")}
                      disabled={disabled}
                    />
                  )}
                />
                {errors.gender?.message ? (
                  <p className={fieldErrorMessageClass}>{errors.gender.message}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="language">
                  {t("common.language")} {currentLanguageName}
                </Label>
                <LanguageSwitcher className="w-full" />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Badge
                variant="secondary"
                className="text-sm"
              >
                {roleLabels[role]}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {t("profile.current_role_description")}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
